import React, { useState } from "react";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { DICTIONARY_TYPES, routes } from "consts";
import { PaziaInput } from "components/atoms";
import { dictionariesApi, DictionaryValueApi } from "api/dictionaries/dictionariesApi";
import { DeleteButton, EditButton } from "components/atoms/Buttons";
import { HeaderWithBreadcrumbs } from "components/molecules/HeaderWithBreadcrumbs/HeaderWithBreadbrumbs";
import { CreateNewDictionaryValue } from "./DictionaryForm/CreateNewDictionaryValue";
import { useToaster } from "utils";
import { useDictionary } from "api/hooks";
import { CheckButton } from "components/atoms/Buttons";
import { PageMain } from "components/molecules";
import { PaziaSelect } from "components/atoms";

export const EditDictionaryPage = () => {
  const { dictionaryId } = useParams();
  const { loading, data } = useDictionary(Number(dictionaryId));

  if (loading || !data) return null;
  return (
    <EditDictionaryForm name={data.name} type={data.type} values={data.values} dictionaryId={Number(dictionaryId)} />
  );
};

const schema = yup.object().shape({
  name: yup.string().required(),
  type: yup.number().required(),
});
export type EditDictionaryFormValues = yup.InferType<typeof schema>;

interface EditDictionaryFormProps {
  name: string;
  type: number;
  dictionaryId: number;
  values: DictionaryValueApi[];
}
export const EditDictionaryForm = ({ name, type, values, dictionaryId }: EditDictionaryFormProps) => {
  const [fields, setFields] = useState<DictionaryValueApi[]>(values);
  const { showSuccess, showError, showInfo } = useToaster();
  const methods = useForm<EditDictionaryFormValues>({
    defaultValues: {
      name: name,
      type,
    },
  });

  const { handleSubmit } = methods;

  const onCreateValue = async (dValue: string) => {
    try {
      const res = await dictionariesApi.createValue(Number(dictionaryId), dValue);
      setFields((pv) => [...pv, res.data]);
      showSuccess("Utworzono wartość.");
    } catch (e) {
      //@ts-ignore
      const error = e?.response?.data?.title ?? "Coś poszło nie tak.";
      showError(error);
    }
  };

  const onUpdateSave = (val: DictionaryValueApi) => {
    dictionariesApi.updateValue(dictionaryId, val.id, val.value).then(() => {
      setFields((pv) => {
        return pv.map((x) => (x.id == val.id ? { ...x, value: val.value } : x));
      });
      showSuccess("Zmodyfikowano wartość.");
    });
  };

  const onValueRemove = (valueId: number) => {
    dictionariesApi.deleteValue(Number(dictionaryId), Number(valueId)).then(() => {
      setFields((pv) => [...pv.filter((x) => x.id !== valueId)]);
      showInfo("Usunięto wartość.");
    });
  };
  // TODO Prevent duplicates
  return (
    <PageMain>
      <HeaderWithBreadcrumbs
        breadcrumbs={[{ title: "Słownik", path: routes.DICTIONARIES }, { title: "Dodawanie wartości do słownika" }]}
        headerText="Dodaj wartości"
      />
      <FormProvider {...methods}>
        {/* TODO  */}
        <form onSubmit={handleSubmit(() => {})} className="max-w-xl">
          <div className="flex flex-col gap-2">
            <PaziaInput label="Nazwa" name="name" />
            <PaziaSelect isClearable={false} label="Typ" name="type" options={DICTIONARY_TYPES} />
          </div>
          <div className="mt-6 border-t border-slate-300 dark:border-slate-600">
            <label className="text-md font-big mb-1 mt-2 block">Wartości</label>
            {/* TODO Uniquness */}
            {fields.map((field, index) => (
              <DictionaryValueItem
                key={uuidv4()}
                field={field}
                index={index}
                onUpdate={onUpdateSave}
                onRemove={onValueRemove}
              />
            ))}
            <CreateNewDictionaryValue index={fields.length} onSubmit={onCreateValue} />
          </div>
          {/* <PaziaButton title="Zapisz" type="success" /> */}
        </form>
      </FormProvider>
    </PageMain>
  );
};

interface DictionaryValueItemProps {
  field: DictionaryValueApi;
  index: number;
  onUpdate: (val: DictionaryValueApi) => void;
  onRemove: (valueId: number) => void;
}

const DictionaryValueItem = ({ field, index, onUpdate, onRemove }: DictionaryValueItemProps) => {
  const [inputValue, setInputValue] = useState(field.value);
  const [isEditActive, setIsEditActive] = useState(false);

  const isDirty = isEditActive && field.value !== inputValue;

  const onUpdateHandler = () => {
    if (!isDirty) return setIsEditActive(false);
    // TODO Empty
    onUpdate({ value: inputValue, id: field.id });
  };

  return (
    <div>
      <div className="flex">
        <div className="flex h-[38px] w-[38px] items-center justify-center">{index + 1}</div>
        {isEditActive ? (
          <>
            <PaziaInput
              name={`values.${index}.value` as const}
              inputClassName="w-[10rem]"
              value={inputValue}
              onChange={(x) => setInputValue(x.target.value)}
            />
            <div className="my-auto flex w-16 justify-between pl-2">
              <CheckButton onClick={onUpdateHandler} />
              <DeleteButton onClick={() => onRemove(field.id)} />
            </div>
          </>
        ) : (
          <>
            <span className="text-md my-auto pr-4 font-medium text-black dark:text-slate-200">{inputValue}</span>
            <div className="my-auto flex w-16 justify-between">
              <EditButton onClick={() => setIsEditActive(true)} />
              <DeleteButton onClick={() => onRemove(field.id)} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
